"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { BookOpen, Layers, Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAppShell } from "@/context/AppShellContext";
import { bookApi } from "@/lib/book-api";
import { listKnowledgeBases } from "@/lib/knowledge-api";

interface PickerEntry {
  name: string;
  courses: number;
}

/**
 * App entry: pick a book (knowledge base) first, then land on its dashboard.
 * Legacy `/?session=xxx` deep links still redirect straight through.
 */
export default function EntryPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const { setActiveKnowledge } = useAppShell();
  const [entries, setEntries] = useState<PickerEntry[] | null>(null);

  const go = useCallback(
    (name: string | null) => {
      setActiveKnowledge(name);
      router.replace("/home");
    },
    [router, setActiveKnowledge],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session");
    const capability = params.get("capability");
    const tools = params.getAll("tool");

    // Deep links keep their old behaviour — the picker only fronts a bare `/`.
    if (sessionId || capability || tools.length) {
      let target = sessionId ? `/home/${sessionId}` : "/home";
      const query: string[] = [];
      if (capability) query.push(`capability=${encodeURIComponent(capability)}`);
      tools.forEach((tool) => query.push(`tool=${encodeURIComponent(tool)}`));
      if (query.length) target += `?${query.join("&")}`;
      router.replace(target);
      return;
    }

    let alive = true;
    (async () => {
      const [knowledge, books] = await Promise.allSettled([
        listKnowledgeBases(),
        bookApi.list(),
      ]);
      if (!alive) return;
      const counts = new Map<string, number>();
      if (books.status === "fulfilled") {
        for (const book of books.value.books) {
          for (const name of book.knowledge_bases || []) {
            counts.set(name, (counts.get(name) || 0) + 1);
          }
        }
      }
      const names =
        knowledge.status === "fulfilled"
          ? knowledge.value
              .map((item) => item.name.trim())
              .filter(Boolean)
              .sort((left, right) => left.localeCompare(right, "ko"))
          : [];
      // Nothing to choose between — skip the extra click.
      if (names.length <= 1) {
        setActiveKnowledge(names[0] ?? null);
        router.replace("/home");
        return;
      }
      setEntries(
        names.map((name) => ({ name, courses: counts.get(name) || 0 })),
      );
    })();
    return () => {
      alive = false;
    };
  }, [router, setActiveKnowledge]);

  if (!entries) {
    return (
      <div className="flex h-full items-center justify-center text-[var(--muted-foreground)]">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-full justify-center overflow-y-auto px-8 py-16">
      <div className="w-full max-w-3xl">
        <h1 className="text-[26px] font-semibold text-[var(--foreground)]">
          {t("Which book will you study?")}
        </h1>
        <p className="mt-2 text-[14px] text-[var(--muted-foreground)]">
          {t("Each book has its own knowledge map, courses, notes and chats.")}
        </p>

        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          {entries.map((entry) => (
            <li key={entry.name}>
              <button
                type="button"
                onClick={() => go(entry.name)}
                className="flex w-full items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--card)] px-4 py-4 text-left transition-colors hover:border-[var(--primary)] hover:bg-[var(--background)]"
              >
                <BookOpen className="h-5 w-5 shrink-0 text-[var(--primary)]" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium text-[var(--foreground)]">
                    {entry.name}
                  </span>
                  <span className="mt-0.5 block text-[13.5px] text-[var(--muted-foreground)]">
                    {t("{{count}} courses", { count: entry.courses })}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => go(null)}
            className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-2 text-[13.5px] text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)]"
          >
            <Layers className="h-4 w-4" />
            {t("See all books")}
          </button>
          <button
            type="button"
            onClick={() => router.push("/knowledge")}
            className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-2 text-[13.5px] text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)]"
          >
            <Plus className="h-4 w-4" />
            {t("Add a book")}
          </button>
        </div>
      </div>
    </div>
  );
}
